import { createSlice } from "@reduxjs/toolkit";

import {
  fetchSubmissionById,
  fetchSubmissionsByAssignment,
  executeStudentSubmission,
  recordSubmissionEvaluation,
  overrideSubmissionByAdmin,
} from "./homeworkSubmission.thunk";

const initialState = {
  homeworkSubmissions: [],
  totalPages: 1,
  currentPage: 1,
  total: 0,
  limit: 10,
  loading: false,
  error: null,
  selectedHomeworkSubmission: null,
};

const homeworkSubmissionSlice = createSlice({
  name: "homeworkSubmission",

  initialState,

  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },

    setSelectedHomeworkSubmission: (state, action) => {
      state.selectedHomeworkSubmission = action.payload;
    },

    clearSelectedHomeworkSubmission: (state) => {
      state.selectedHomeworkSubmission = null;
    },

    /* ============================================================
       UI COMPATIBILITY ALIAS REDUCERS
    ============================================================ */
    setSelectedSubmission: (state, action) => {
      state.selectedHomeworkSubmission = action.payload;
    },

    clearSelectedSubmission: (state) => {
      state.selectedHomeworkSubmission = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* ============================================================
         FETCH GLOBAL TENANT HOMEWORK SUBMISSION LIST
      ============================================================ */
      .addCase(fetchSubmissionsByAssignment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchSubmissionsByAssignment.fulfilled, (state, action) => {
        state.loading = false;
        state.homeworkSubmissions = action.payload?.data || [];
        
        // Dynamic payload parsing for paginated institutional data records
        const meta = action.payload?.meta || {};
        state.totalPages = meta.total_pages || 1;
        state.total = meta.total || (action.payload?.data?.length || 0);
        state.limit = meta.limit || 10;
      })

      .addCase(fetchSubmissionsByAssignment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         FETCH SINGLE HOMEWORK SUBMISSION BY ID
      ============================================================ */
      .addCase(fetchSubmissionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchSubmissionById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedHomeworkSubmission = action.payload;
      })

      .addCase(fetchSubmissionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         EXECUTE STUDENT SUBMISSION
      ============================================================ */
      .addCase(executeStudentSubmission.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(executeStudentSubmission.fulfilled, (state) => {
        state.loading = false;
      })

      .addCase(executeStudentSubmission.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         RECORD SUBMISSION EVALUATION (TEACHER)
      ============================================================ */
      .addCase(recordSubmissionEvaluation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(recordSubmissionEvaluation.fulfilled, (state, action) => {
        state.loading = false;
        const { id, updatedMetrics } = action.payload;

        // Optimistically update list array element parameters
        state.homeworkSubmissions = state.homeworkSubmissions.map((submission) =>
          submission.id === id
            ? {
                ...submission,
                submission_status: updatedMetrics.submission_status || "Reviewed",
                teacher_feedback: updatedMetrics.teacher_feedback,
              }
            : submission
        );

        // Synchronize selected drill-down view object parameters
        if (state.selectedHomeworkSubmission && state.selectedHomeworkSubmission.id === id) {
          state.selectedHomeworkSubmission.submission_status = updatedMetrics.submission_status || "Reviewed";
          state.selectedHomeworkSubmission.teacher_feedback = updatedMetrics.teacher_feedback;
        }
      })

      .addCase(recordSubmissionEvaluation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* ============================================================
         OVERRIDE SUBMISSION BY ADMIN
      ============================================================ */
      .addCase(overrideSubmissionByAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(overrideSubmissionByAdmin.fulfilled, (state, action) => {
        state.loading = false;
        const { id, updatedMetrics } = action.payload;

        // Optimistically update list array element parameters
        state.homeworkSubmissions = state.homeworkSubmissions.map((submission) =>
          submission.id === id
            ? {
                ...submission,
                submission_status: updatedMetrics.submission_status || submission.submission_status,
                admin_notes: updatedMetrics.admin_notes,
              }
            : submission
        );

        // Synchronize selected drill-down view object parameters
        if (state.selectedHomeworkSubmission && state.selectedHomeworkSubmission.id === id) {
          if (updatedMetrics.submission_status) {
            state.selectedHomeworkSubmission.submission_status = updatedMetrics.submission_status;
          }
          state.selectedHomeworkSubmission.admin_notes = updatedMetrics.admin_notes;
        }
      })

      .addCase(overrideSubmissionByAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setCurrentPage,
  setSelectedHomeworkSubmission,
  clearSelectedHomeworkSubmission,
  setSelectedSubmission,
  clearSelectedSubmission,
} = homeworkSubmissionSlice.actions;

export default homeworkSubmissionSlice.reducer;